import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Clock, 
  Loader2, 
  ChevronDown,
  ExternalLink,
  Filter,
  X
} from 'lucide-react';
import { useSearch } from '../../contexts/SearchContext';
import { SearchResultItem } from './SearchResultItem';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface SearchResultsProps {
  isDropdown?: boolean;
  maxResults?: number;
  showLoadMore?: boolean;
  onClose?: () => void;
  hideFilters?: boolean;
}

export function SearchResults({ 
  isDropdown = false, 
  maxResults = 10,
  showLoadMore = true,
  onClose,
  hideFilters = false
}: SearchResultsProps) {
  const {
    query,
    results,
    loading,
    error,
    totalCount,
    hasMore,
    recentSearches,
    suggestions,
    isSearchOpen,
    setQuery,
    clearSearch,
    navigateToResult,
    loadMoreResults,
    addToRecentSearches
  } = useSearch();

  const isMobile = useIsMobile();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showFilters, setShowFilters] = useState(false);

  // Keyboard navigation (only for desktop)
  useEffect(() => {
    if (!isDropdown || !isSearchOpen || isMobile) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            navigateToResult(results[selectedIndex]);
          } else if (query.trim()) {
            // Navigate to full search page
            window.location.href = `/dashboard/search?q=${encodeURIComponent(query.trim())}`;
          }
          break;
        case 'Escape':
          onClose?.();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDropdown, isSearchOpen, isMobile, selectedIndex, results, query, navigateToResult, onClose]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  const handleResultClick = useCallback((result: any) => {
    navigateToResult(result);
  }, [navigateToResult]);

  const handleRecentSearchClick = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
  }, [setQuery]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setQuery(suggestion);
    addToRecentSearches(suggestion);
  }, [setQuery, addToRecentSearches]);

  const handleViewAllResults = useCallback(() => {
    if (query.trim()) {
      window.location.href = `/dashboard/search?q=${encodeURIComponent(query.trim())}`;
    }
  }, [query]);

  const displayResults = isDropdown ? results.slice(0, maxResults) : results;
  const hasMoreToShow = isDropdown && results.length > maxResults;

  // Show empty state with suggestions
  const showEmptyState = !query.trim() && !loading;
  const showNoResults = query.trim() && !loading && results.length === 0 && !error;

  return (
    <AnimatePresence>
      {(isSearchOpen || !isDropdown) && (
        <motion.div
          initial={{ opacity: 0, y: isDropdown ? -10 : 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: isDropdown ? -10 : 0 }}
          className={`
            bg-white rounded-lg shadow-lg border border-gray-200
            ${isDropdown ? 'absolute top-full left-0 right-0 mt-2 z-50 max-h-96 overflow-hidden' : 'w-full'}
            ${isMobile && isDropdown ? 'fixed inset-x-4 top-20 z-50 max-h-[80vh]' : ''}
          `}
          data-search-dropdown={isDropdown ? 'true' : undefined}
        >
          {/* Header for full page view */}
          {!isDropdown && (
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Search className="w-5 h-5 text-gray-400" />
                <span className="text-lg font-semibold text-gray-900">
                  Search Results
                </span>
                {totalCount > 0 && (
                  <span className="text-sm text-gray-500">
                    ({totalCount} results)
                  </span>
                )}
              </div>
              
              {!hideFilters && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                </button>
              )}
            </div>
          )}

          <div className={isDropdown ? 'max-h-96 overflow-y-auto' : ''}>
            {/* Loading state */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                <span className="ml-2 text-gray-600">Searching...</span>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="px-4 py-6 text-center">
                <div className="text-red-600 mb-2">Search Error</div>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            )}

            {/* Empty state with recent searches and suggestions */}
            {showEmptyState && (
              <div className="px-4 py-6">
                {/* Recent searches */}
                {recentSearches.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center text-sm font-medium text-gray-700 mb-3">
                      <Clock className="w-4 h-4 mr-2" />
                      Recent searches
                    </div>
                    <div className="space-y-1">
                      {recentSearches.slice(0, 5).map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentSearchClick(search)}
                          className={`block w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors ${
                            isMobile ? 'min-h-[44px] flex items-center' : ''
                          }`}
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search tips */}
                <div className="text-sm text-gray-500">
                  <p className="font-medium mb-2">Search tips:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Search for projects, creatives, or analyses</li>
                    <li>Use keywords from file names or project names</li>
                    <li>Try searching for specific platform names</li>
                  </ul>
                </div>
              </div>
            )}

            {/* No results state */}
            {showNoResults && (
              <div className="px-4 py-6 text-center">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <div className="text-gray-900 font-medium mb-2">No results found</div>
                <p className="text-sm text-gray-600 mb-4">
                  No results found for "{query}". Try a different search term.
                </p>
                
                {/* Suggestions if available */}
                {suggestions.length > 0 && (
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Did you mean:
                    </p>
                    <div className="space-y-1">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`block w-full text-left px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors ${
                            isMobile ? 'min-h-[44px] flex items-center' : ''
                          }`}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Search results */}
            {displayResults.length > 0 && (
              <div className="divide-y divide-gray-100">
                {displayResults.map((result, index) => (
                  <div key={result.id} className="group">
                    <SearchResultItem
                      result={result}
                      onClick={handleResultClick}
                      isHighlighted={selectedIndex === index}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Footer for dropdown */}
            {isDropdown && results.length > 0 && (
              <div className="border-t border-gray-100 px-4 py-3">
                {hasMoreToShow && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      Showing {Math.min(maxResults, results.length)} of {totalCount} results
                    </span>
                    <button
                      onClick={handleViewAllResults}
                      className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      <span>View all results</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {!hasMoreToShow && hasMore && showLoadMore && (
                  <button
                    onClick={loadMoreResults}
                    disabled={loading}
                    className="flex items-center space-x-2 text-sm text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                    <span>{loading ? 'Loading...' : 'Load more results'}</span>
                  </button>
                )}
                
                {!isMobile && (
                  <div className="text-xs text-gray-400 mt-2 text-center">
                    Press ↑ ↓ to navigate, ↵ to select, ESC to close
                  </div>
                )}
              </div>
            )}

            {/* Load more button for full page */}
            {!isDropdown && hasMore && showLoadMore && (
              <div className="px-4 py-4 text-center border-t border-gray-100">
                <button
                  onClick={loadMoreResults}
                  disabled={loading}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <span>{loading ? 'Loading...' : 'Load more results'}</span>
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}